<template>
    <div>
        <Label
            :label="props.label"
            :input-id="inputId"
            :help-button-text="props.helpButtonText"
            :is-required="props.required"
            :is-disabled="props.disabled"
        />
        <div class="relative mt-1">
            <input
                :id="inputId"
                ref="inputElement"
                :value="value"
                type="text"
                :disabled="disabled"
                :autocomplete="autocomplete"
                :minLength="minLength"
                :maxLength="maxLength"
                class="block py-2.25 w-full rounded-md shadow-xs text-sm disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500"
                :class="[
                    errorMessages && errorMessages.length
                        ? 'focus:outline-hidden border-red-500 text-red-700 placeholder-red-300 focus:ring-red-600 focus:border-red-500'
                        : 'focus:ring-indigo-500 focus:border-indigo-500 border-gray-300',
                ]"
                :style="paddingRightStyle"
                :required="required"
                :placeholder="placeholder"
                :aria-invalid="errorMessages && errorMessages.length ? true : undefined"
                :aria-describedby="description ? `${inputId}-info` : undefined"
                :aria-errormessage="errorMessages && errorMessages.length ? `${inputId}-error` : undefined"
                @input="$emit('input', $event)"
                @change="$emit('change', $event)"
                @blur="$emit('blur', $event)"
            />
            <div
                ref="trailingElement"
                class="pointer-events-none absolute inset-y-0 right-0 flex items-center pl-2 pr-1.5 gap-1"
            >
                <WarningIcon
                    v-if="errorMessages && errorMessages.length"
                    class="size-5 mr-1 text-red-600"
                    aria-hidden="true"
                />
                <Tooltip
                    v-for="preset in presets"
                    :key="preset.value"
                    :is-disabled="disabled"
                    :is-teleported="true"
                    class="pointer-events-auto"
                >
                    <template #trigger="tooltipProps">
                        <button
                            v-bind="tooltipProps"
                            type="button"
                            :disabled="disabled"
                            :aria-pressed="value === preset.value"
                            class="flex items-center h-7 px-2 rounded-xs text-white cursor-pointer transition-colors duration-200 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                            :class="[
                                value === preset.value
                                    ? preset.selectedClass
                                    : 'bg-gray-400 enabled:hover:bg-gray-500 enabled:active:bg-gray-600',
                                { 'snap-in': snappedValue === preset.value },
                            ]"
                            @click="fill(preset.value)"
                            @animationend="snappedValue = undefined"
                        >
                            <component :is="preset.icon" :class="preset.iconClass" />
                        </button>
                    </template>
                    <template #text>
                        {{
                            value === preset.value
                                ? $t('preset-already-filled')
                                : $t('fill-preset', { value: preset.value })
                        }}
                    </template>
                </Tooltip>
            </div>
        </div>

        <div
            v-if="errorMessages && errorMessages.length"
            :id="`${inputId}-error`"
            role="alert"
            class="mt-1 text-sm text-red-600"
        >
            <p v-for="message in errorMessages" :key="message">{{ message }}</p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useElementSize } from '@vueuse/core';
import { nanoid } from 'nanoid';
import { type Component, computed, provide, ref, useTemplateRef, watch } from 'vue';
import WarningIcon from '../Icon/WarningIcon.vue';
import { triggerHideTooltipKey } from '../Tooltip/injectionKeys';
import Tooltip from '../Tooltip/Tooltip.vue';
import Label from './Label.vue';
import { TextFieldAutoCompleteOptions } from './TextField.vue';

export type Preset = {
    value: string;
    icon: Component;
    iconClass?: string;
    selectedClass: string;
};

type PresetTextFieldProps = {
    value: string;
    label: string;
    presets: Preset[];
    disabled?: boolean;
    description?: string;
    helpButtonText?: string;
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    errorMessages?: string[];
    placeholder?: string;
    autocomplete?: TextFieldAutoCompleteOptions;
};

const props = defineProps<PresetTextFieldProps>();

defineEmits<{ (e: 'input', v: Event): void; (e: 'change', v: Event): void; (e: 'blur', v: Event): void }>();

const inputId = nanoid();
const inputElement = useTemplateRef<HTMLInputElement>('inputElement');
const trailingElement = useTemplateRef<HTMLDivElement>('trailingElement');
const { width: trailingWidth } = useElementSize(trailingElement, undefined, { box: 'border-box' });

const triggerHideTooltipKeyCounter = ref(0);
provide(triggerHideTooltipKey, triggerHideTooltipKeyCounter);

// Dispatching native events lets consumers handle a button fill exactly like typed input
function fill(value: string) {
    triggerHideTooltipKeyCounter.value++;

    const input = inputElement.value;
    if (!input) {
        throw new Error('PresetTextField: input element not mounted');
    }
    input.value = value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
}

const snappedValue = ref<string>();
watch(
    () => props.value,
    (value) => {
        if (props.presets.some((preset) => preset.value === value)) {
            snappedValue.value = value;
        }
    },
);

const paddingRightStyle = computed(() => {
    return trailingWidth.value ? `padding-right: ${trailingWidth.value}px` : undefined;
});
</script>

<style scoped>
@media (prefers-reduced-motion: no-preference) {
    .snap-in {
        animation: snap-in 400ms ease-out;
    }
}

@keyframes snap-in {
    50% {
        transform: scale(1.05);
    }
}
</style>
